import { useEffect, ReactNode } from "react"
import { BlackFridayProvider } from "../../context/blackFridayContext"
import { PurchasProvider } from "../../context/purchaseContext"
import { useAppState } from "../../context/appStateContext"
import FullScreenPage from "../../components/FullScreenPage/FullScreenPage"
import useStrings from "../../localization/useStrings"
import native from "../../native/native"
import styles from "./SeasonalPaywall.module.scss"
import CloseButton from "../../components/CloseButton/CloseButton"
import usePromoCountdown from "../../context/usePromoCountdown"
import { useBlackFriday } from "../../context/blackFridayContext"
import FeaturesList from "./components/FeaturesList/FeaturesList"
import UltimateFeaturesList from "./components/UltimateFeaturesList/UltimateFeaturesList"
import svg from "../../utils/svg"
import { UltimateAlertsProvider } from "../../context/ultimateAlertsContext"
import UltimateSubfeaturesPopups from "../../components/UltimateSubfeaturesPopups/UltimateSubfeaturesPopups"
import { usePricingData } from "../../hooks/usePricingData"

export interface HeaderColors {
  background: string
  title: string
  tagline: string
  boxShadow?: string
  border?: string
  borderRadius?: string
  margin?: string
}

export interface CountdownColors {
  boxBackground: string
  value: string
  unit: string
  label: string
}

export interface ContentColors {
  background: string
  ultimateContainerBackground: string
  ultimateContainerText: string
  ultimateContainerMarginTop?: string
}

export interface FeatureColors {
  icon: string
  title: string
  description: string
  infoButton?: string
}

export interface PrimaryButtonColors {
  background: string
  headerBackground: string
  contentBackground: string
  limitedTimeText: string
  saveTagText: string
  planTitle: string
  duration: string
  originalPrice: string
  discountedPrice: string
  monthlyRate: string
}

export interface MonthlyButtonColors {
  background: string
  border: string
  label: string
  price: string
}

export interface FooterColors {
  background: string
  primaryButton: PrimaryButtonColors
  monthlyButton: MonthlyButtonColors
  boxShadow?: string
  borderTop?: string
  borderRadius?: string
}

export interface InsetColors {
  header: string
  footer: string
}

export interface SeasonalPaywallColors {
  scrollableGradient: string
  header?: HeaderColors
  countdown?: CountdownColors
  content: ContentColors
  features: FeatureColors
  footer: FooterColors
  insets: InsetColors
  closeButton?: string
}

export interface SeasonalPaywallStrings {
  title: string
  tagline: string
  buttonTitle: string
}

interface SeasonalPaywallProps {
  topDecoration?: ReactNode
  headerBackground?: ReactNode
  footerBackground?: ReactNode
  customHeader?: ReactNode
  colors: SeasonalPaywallColors
  strings: SeasonalPaywallStrings
  headerSpacing?: string
  topInsetPercent?: number // 0 = fully ignored, 100 = fully shown
}

const Countdown = ({ colors }: { colors: SeasonalPaywallColors }) => {
  const { minutes, hours, seconds } = usePromoCountdown()
  const strings = useStrings()

  if (!colors.countdown) return null

  return (
    <div className={styles.countdown}>
      <div className={styles.countdownBoxes}>
        <CountdownItem
          value={hours}
          unit={strings.summerSale.hours}
          colors={colors}
        />
        <CountdownItem
          value={minutes}
          unit={strings.summerSale.minutes}
          colors={colors}
        />
        <CountdownItem
          value={seconds}
          unit={strings.summerSale.seconds}
          colors={colors}
        />
      </div>
      <div
        className={styles.countdownLabel}
        style={{ color: colors.countdown.label }}
      >
        {strings.summerSale.timeRemaining}
      </div>
    </div>
  )
}

const CountdownItem = (props: {
  value: number
  unit: string
  colors: SeasonalPaywallColors
}) => {
  if (!props.colors.countdown) return null

  return (
    <div
      className={styles.countdownItem}
      style={{ background: props.colors.countdown.boxBackground }}
    >
      <div
        className={styles.countdownValue}
        style={{ color: props.colors.countdown.value }}
      >
        {props.value.toString().padStart(2, "0")}
      </div>
      <div
        className={styles.countdownUnit}
        style={{ color: props.colors.countdown.unit }}
      >
        {props.unit}
      </div>
    </div>
  )
}

const Footer = ({
  colors,
  customStrings,
}: {
  colors: SeasonalPaywallColors
  customStrings: SeasonalPaywallStrings
}) => {
  const { actions } = useBlackFriday()
  const defaultStrings = useStrings()
  const { discountedPrice, yearlyPerMonthPrice, monthlyPrice, savePercent } =
    usePricingData()

  return (
    <div className={styles.footerContainer}>
      <button
        className={styles.summerSaleButton}
        style={{ background: colors.footer.primaryButton.background }}
        onClick={() => actions.purchaseYearly()}
      >
        <div
          className={styles.summerSaleHeader}
          style={{ background: colors.footer.primaryButton.headerBackground }}
        >
          <span
            className={styles.limitedTime}
            style={{ color: colors.footer.primaryButton.limitedTimeText }}
          >
            {defaultStrings.summerSale.limitedTimeOffer}
          </span>
          <span
            className={styles.saveTag}
            style={{ color: colors.footer.primaryButton.saveTagText }}
          >
            {defaultStrings.summerSale.savePercent(savePercent)}
          </span>
        </div>

        <div
          className={styles.summerSaleContent}
          style={{ background: colors.footer.primaryButton.contentBackground }}
        >
          <div className={styles.planInfo}>
            <h3
              className={styles.planTitle}
              style={{ color: colors.footer.primaryButton.planTitle }}
            >
              {customStrings.buttonTitle}
            </h3>
            <div
              className={styles.duration}
              style={{ color: colors.footer.primaryButton.duration }}
            >
              <span>{defaultStrings.bubbleExpiration.yearly}</span>
            </div>
          </div>

          <div className={styles.priceDisplay}>
            <span
              className={styles.monthlyRate}
              style={{ color: colors.footer.primaryButton.monthlyRate }}
            >
              {defaultStrings.promo.yearlyShort(discountedPrice)}
            </span>
            <span className={styles.perMonthSub}>
              {`(${defaultStrings.summerSale.pricing.perMonth(yearlyPerMonthPrice)})`}
            </span>
          </div>
        </div>
      </button>

      <button
        className={styles.monthlyButton}
        style={{
          background: colors.footer.monthlyButton.background,
          border: colors.footer.monthlyButton.border,
        }}
        onClick={() => actions.purchaseMonthly()}
      >
        <span
          className={styles.monthlyLabel}
          style={{ color: colors.footer.monthlyButton.label }}
        >
          {defaultStrings.summerSale.pricing.monthly}
        </span>
        <span
          className={styles.monthlyPrice}
          style={{ color: colors.footer.monthlyButton.price }}
        >
          {defaultStrings.summerSale.pricing.perMonth(monthlyPrice)}
        </span>
      </button>
    </div>
  )
}

const SeasonalPaywallContent = ({
  topDecoration,
  headerBackground,
  footerBackground,
  customHeader,
  colors,
  strings: customStrings,
  headerSpacing,
  topInsetPercent = 100,
}: SeasonalPaywallProps) => {
  const { actions } = useAppState()
  const defaultStrings = useStrings()
  const { state } = useBlackFriday()

  const { initialData } = useAppState().state

  const paddingTop = initialData?.originalTopInset ?? 0
  const paddingBottom = initialData?.originalBottomInset ?? 0

  useEffect(() => {
    actions.setHeaderInsetColor(colors.insets.header)
    actions.setFooterInsetColor(colors.insets.footer)
    actions.setIgnoreHeaderInset(true)
    actions.setIgnoreFooterInset(true)
  }, [actions, colors.insets.header, colors.insets.footer])

  return (
    <FullScreenPage containerClassName={styles.page} addInsetPadding={false}>
      <UltimateSubfeaturesPopups />
      <CloseButton
        className={styles.closeButton}
        style={{ top: paddingTop + 16 }}
        onClick={() => {
          native.postNativeEvent("close")
        }}
        color={colors.closeButton}
      />
      <div className={styles.container}>
        <div
          className={styles.scrollable}
          style={{
            background: colors.scrollableGradient,
            paddingTop: (paddingTop * topInsetPercent) / 100,
          }}
        >
          {topDecoration && (
            <div className={styles.topDecoration}>{topDecoration}</div>
          )}
          {customHeader ? (
            customHeader
          ) : (
            <div
              className={styles.header}
              style={{
                background: colors.header?.background,
                ...(colors.header?.boxShadow && {
                  boxShadow: colors.header.boxShadow,
                  overflow: "visible",
                }),
                ...(colors.header?.border && { border: colors.header.border }),
                ...(colors.header?.borderRadius && {
                  borderRadius: colors.header.borderRadius,
                }),
                ...(colors.header?.margin && { margin: colors.header.margin }),
                ...(headerSpacing && { marginBottom: headerSpacing }),
              }}
            >
              {headerBackground}
              <div className={styles.headerContent}>
                <h1
                  className={styles.title}
                  style={{ color: colors.header?.title }}
                >
                  {customStrings.title}
                </h1>
                <p
                  className={styles.tagline}
                  style={{ color: colors.header?.tagline }}
                >
                  {customStrings.tagline}
                </p>
                <Countdown colors={colors} />
              </div>
            </div>
          )}
          <div
            className={styles.content}
            style={{ background: colors.content.background }}
          >
            {(state.isPremiumUser || state.isUltimate) && (
              <div className={styles.ultimateContentWrapper}>
                <div
                  className={styles.goUltimateContainer}
                  style={{
                    backgroundColor: colors.content.ultimateContainerBackground,
                    color: colors.content.ultimateContainerText,
                    ...(colors.content.ultimateContainerMarginTop && {
                      marginTop: colors.content.ultimateContainerMarginTop,
                    }),
                  }}
                >
                  <label>
                    {defaultStrings.premiumToUltimateBlackFriday.subTitle}
                  </label>
                  <svg.UltimateIcon
                    width={14}
                    height={11}
                    fill={colors.content.ultimateContainerText}
                  />
                </div>
                <UltimateFeaturesList colors={colors} />
              </div>
            )}
            {!state.isPremiumUser && !state.isUltimate && (
              <FeaturesList colors={colors} />
            )}
          </div>
        </div>
        <div
          className={styles.footer}
          style={{
            paddingBottom: paddingBottom + 16,
            backgroundColor: colors.footer.background,
            ...(colors.footer.boxShadow && {
              boxShadow: colors.footer.boxShadow,
            }),
            ...(colors.footer.borderTop && {
              borderTop: colors.footer.borderTop,
            }),
            ...(colors.footer.borderRadius && {
              borderRadius: colors.footer.borderRadius,
            }),
          }}
        >
          {footerBackground}
          <Footer colors={colors} customStrings={customStrings} />
        </div>
      </div>
    </FullScreenPage>
  )
}

const SeasonalPaywall = (props: SeasonalPaywallProps) => {
  return (
    <PurchasProvider>
      <UltimateAlertsProvider>
        <SeasonalPaywallContent {...props} />
      </UltimateAlertsProvider>
    </PurchasProvider>
  )
}

export default SeasonalPaywall
